import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from "../includes/sidebar";
import { Topbar } from "../includes/topbar";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Progress } from "../../components/ui/progress";
import { comptesApi, Compte } from "../../api/clients.api";
import { transactionsApi, Transaction, FeeConfig, GroupedTransactionItem } from "../../api/transactions.api";
import { useToast } from "../../hooks/use-toast";
import {
    Search,
    Loader2,
    User,
    CreditCard,
    Wallet,
    ArrowDownLeft,
    ArrowUpRight,
    CheckCircle2,
    AlertCircle,
    Calendar,
    Clock,
    ArrowLeft,
    HandMetal,
    TrendingUp,
    RefreshCcw,
    Flame
} from "lucide-react";

type TransactionType = 'depo_bs' | 'retrait_bs' | 'depo_sol';

// ... (inside component)



interface TransactionOption {
    value: TransactionType;
    label: string;
    icon: React.ReactNode;
}

export default function NewTransactionPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { toast } = useToast();
    const { t, i18n } = useTranslation(['transactions', 'common']);

    // Account search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Compte[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<Compte | null>(null);
    const [isLoadingAccount, setIsLoadingAccount] = useState(false);

    // Transactions history (supports grouped SOL transactions)
    const [lastTransactions, setLastTransactions] = useState<GroupedTransactionItem[]>([]);
    const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    // Transaction form state
    const [transactionType, setTransactionType] = useState<TransactionType | ''>('');
    const [amount, setAmount] = useState('');
    const [nbMain, setNbMain] = useState('');
    const [useNbMain, setUseNbMain] = useState(false);

    // Submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Fee configuration state
    const [feeConfig, setFeeConfig] = useState<FeeConfig | null>(null);
    const feePercentage = feeConfig ? parseFloat(feeConfig.percentage) : 1; // Default 1%
    const feeAmount = transactionType === 'retrait_bs' && amount ? (parseFloat(amount) * feePercentage / 100) : 0;
    const totalDebit = transactionType === 'retrait_bs' && amount ? parseFloat(amount) + feeAmount : parseFloat(amount || '0');

    // Get transaction types based on account type
    const getTransactionOptions = (accountType: string | undefined): TransactionOption[] => {
        const type = accountType?.toUpperCase();
        if (type === 'SOL') {
            return [
                { value: 'depo_sol', label: t('newTransaction.transactionTypes.depo_sol'), icon: <ArrowDownLeft className="h-4 w-4 text-green-500" /> },
            ];
        } else if (type === 'BS') {
            return [
                { value: 'depo_bs', label: t('newTransaction.transactionTypes.depo_bs'), icon: <ArrowDownLeft className="h-4 w-4 text-green-500" /> },
                { value: 'retrait_bs', label: t('newTransaction.transactionTypes.retrait_bs'), icon: <ArrowUpRight className="h-4 w-4 text-red-500" /> },
            ];
        }
        return [];
    };

    // Load account from URL param if present
    useEffect(() => {
        const compteId = searchParams.get('compte');
        if (compteId) {
            loadAccountById(compteId);
        }
    }, [searchParams]);

    // Load account by ID
    const loadAccountById = async (id: string) => {
        setIsLoadingAccount(true);
        try {
            const response = await comptesApi.getById(id);
            setSelectedAccount(response.compte);
        } catch (error) {
            console.error('Error loading account:', error);
            toast({ title: t('error'), description: t('newTransaction.errors.accountLoadFailed'), type: "error" });
        } finally {
            setIsLoadingAccount(false);
        }
    };

    // Load last transactions for selected account (with SOL grouping)
    const loadLastTransactions = useCallback(async (compteId: string) => {
        setIsLoadingTransactions(true);
        setExpandedGroups(new Set()); // Reset expanded groups
        try {
            const response = await transactionsApi.getByCompte(compteId, { limit: 6 });
            setLastTransactions(response);
        } catch (error) {
            console.error('Error loading transactions:', error);
        } finally {
            setIsLoadingTransactions(false);
        }
    }, []);

    // Toggle group expansion
    const toggleGroupExpansion = (groupKey: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
    };

    // Load transactions when account is selected
    useEffect(() => {
        if (selectedAccount) {
            loadLastTransactions(selectedAccount.id);
            const options = getTransactionOptions(selectedAccount.type || selectedAccount.type_compte);
            if (options.length > 0) {
                setTransactionType(options[0].value);
            }
        }
    }, [selectedAccount, loadLastTransactions]);

    // Fetch fee config when transaction type is retrait_bs
    useEffect(() => {
        const fetchFeeConfig = async () => {
            if (transactionType === 'retrait_bs') {
                try {
                    const config = await transactionsApi.getFeeConfig('retrait_bs');
                    setFeeConfig(config);
                } catch (error) {
                    console.warn('Failed to fetch fee config, defaulting to null', error);
                    setFeeConfig(null);
                }
            } else {
                setFeeConfig(null);
            }
        };
        fetchFeeConfig();
    }, [transactionType]);

    // Search accounts
    const handleSearch = useCallback(async () => {
        if (searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const response = await comptesApi.list({ search: searchQuery, limit: 10 });
            // Filter out clients that were created online and are pending verification
            const filteredResults = response.comptes.filter(compte => {
                const client = compte.client;
                if (client?.onlineCreated && client?.statut === 'pending_verification') {
                    return false;
                }
                return true;
            });
            setSearchResults(filteredResults);
        } catch (error) {
            console.error('Search error:', error);
            toast({ title: t('newTransaction.errors.search'), description: t('newTransaction.errors.searchDescription'), type: "error" });
        } finally {
            setIsSearching(false);
        }
    }, [searchQuery, toast]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery.length >= 2) {
                handleSearch();
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery, handleSearch]);

    // Calculate amount from nb_main for SOL accounts
    useEffect(() => {
        const montantJours = selectedAccount?.montantJours || selectedAccount?.montant_jours;
        if (useNbMain && selectedAccount && nbMain && montantJours) {
            const calculatedAmount = parseFloat(nbMain) * montantJours;
            setAmount(calculatedAmount.toString());
        }
    }, [nbMain, useNbMain, selectedAccount]);

    // Select account from search results and load full details
    const handleSelectAccount = async (compte: Compte) => {
        setSearchResults([]);
        setSearchQuery('');
        setErrors({});
        setIsLoadingAccount(true);

        try {
            // Load full account details with all SOL progress fields using the details endpoint
            const details = await comptesApi.getAccountDetails(compte.code);
            // Normalize type if missing
            if (!details.type && details.type_compte) {
                details.type = details.type_compte.toUpperCase() === 'SOL' ? 'SOL' : 'BS';
            }
            // Ensure type is uppercase for consistency
            if (details.type) {
                details.type = details.type.toUpperCase() as any;
            }

            setSelectedAccount(details);
        } catch (error) {
            console.error('Error loading account details:', error);
            // Fallback to search result data if details fetch fails
            setSelectedAccount(compte);
            toast({ title: t('common:warning'), description: t('newTransaction.errors.details'), type: "default" });
        } finally {
            setIsLoadingAccount(false);
        }
    };

    // Format amount for display
    const formatAmount = (value: number, devise: string = 'HTG') => {
        return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(value) + ' ' + devise;
    };

    // Format date
    const formatDate = (dateStr: string): string => {
        return new Date(dateStr).toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // Get type label
    const getTypeLabel = (type: string): string => {
        return t(`types.${type}`, { defaultValue: type });
    };

    // State for expanded SOL transaction groups
    const [expandedSolGroups, setExpandedSolGroups] = useState<Set<string>>(new Set());

    // Toggle expansion of a SOL transaction group
    const toggleSolGroup = (groupKey: string) => {
        setExpandedSolGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
    };

    // Group depo_sol transactions by date_sol (same day) and prepare display data
    interface GroupedTransaction {
        type: 'single' | 'group';
        transaction?: Transaction;
        groupKey?: string;
        transactions?: Transaction[];
        totalMontant?: number;
        count?: number;
        latestCreatedAt?: string;
    }

    const getGroupedTransactions = (transactions: Transaction[]): GroupedTransaction[] => {
        const result: GroupedTransaction[] = [];
        const solGroups: Map<string, Transaction[]> = new Map();

        // Separate SOL transactions and group by date_sol
        for (const tx of transactions) {
            if (tx.type === 'depo_sol') {
                const key = tx.date_sol || tx.created_at.split('T')[0];
                if (!solGroups.has(key)) solGroups.set(key, []);
                solGroups.get(key)!.push(tx);
            } else {
                result.push({ type: 'single', transaction: tx });
            }
        }

        // Convert SOL groups to display items
        solGroups.forEach((txs, groupKey) => {
            if (txs.length === 1) {
                // Single transaction, show as normal
                result.push({ type: 'single', transaction: txs[0] });
            } else {
                // Multiple transactions, show as group
                const totalMontant = txs.reduce((sum, tx) => sum + parseFloat(tx.montant), 0);
                const latestTx = txs.sort((a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )[0];
                result.push({
                    type: 'group',
                    groupKey,
                    transactions: txs,
                    totalMontant,
                    count: txs.length,
                    latestCreatedAt: latestTx.created_at,
                });
            }
        });

        // Sort by most recent created_at
        return result.sort((a, b) => {
            const dateA = a.type === 'single' ? a.transaction!.created_at : a.latestCreatedAt!;
            const dateB = b.type === 'single' ? b.transaction!.created_at : b.latestCreatedAt!;
            return new Date(dateB).getTime() - new Date(dateA).getTime();
        });
    };

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!selectedAccount) {
            newErrors.account = t('newTransaction.errors.validation.account');
        }
        if (!transactionType) {
            newErrors.type = t('newTransaction.errors.validation.type');
        }
        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = t('newTransaction.errors.validation.amount');
        }

        if (useNbMain && (!nbMain || parseFloat(nbMain) <= 0)) {
            newErrors.amount = t('newTransaction.errors.validation.nbMain');
        }

        if (transactionType === 'retrait_bs' && selectedAccount) {
            if (parseFloat(amount) > selectedAccount.soldeBS) {
                newErrors.amount = `Montan pi gwo pase balans (${formatAmount(selectedAccount.soldeBS)})`;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        setShowConfirmation(true);
    };

    // Execute transaction
    const executeTransaction = async () => {
        if (!selectedAccount || !transactionType) return;

        setShowConfirmation(false);
        setIsSubmitting(true);

        try {
            if (transactionType === 'depo_bs') {
                await transactionsApi.deposit({
                    compte_id: selectedAccount.id,
                    compte_code: selectedAccount.code,
                    montant: parseFloat(amount),
                    locale: i18n.language,
                });
            } else if (transactionType === 'retrait_bs') {
                await transactionsApi.withdraw({
                    compte_id: selectedAccount.id,
                    compte_code: selectedAccount.code,
                    montant: parseFloat(amount),
                    locale: i18n.language,
                });
            } else if (transactionType === 'depo_sol') {
                await transactionsApi.depositSol({
                    compte_id: selectedAccount.id,
                    compte_code: selectedAccount.code,
                    montant: parseFloat(amount),
                    locale: i18n.language,
                });
            }

            toast({
                title: t('common:success'),
                description: t('newTransaction.success.created'),
                type: "success"
            });

            // Reload transactions and reset form
            loadLastTransactions(selectedAccount.id);
            setAmount('');
            setNbMain('');

        } catch (error: any) {
            console.error('Transaction error:', error);
            toast({
                title: "Erè",
                description: error.response?.data?.error || error.message || "Tranzaksyon an echwe.",
                type: "error"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const transactionOptions = selectedAccount ? getTransactionOptions(selectedAccount.type || selectedAccount.type_compte) : [];
    const dernierTransaction = lastTransactions[0] || null;

    // Calculate SOL progress using correct backend field names (snake_case)
    const getSolProgress = () => {
        if (!selectedAccount) return null;
        // Check account type (backend sends type_compte)
        const accountType = selectedAccount.type || selectedAccount.type_compte;
        if (accountType !== 'SOL') return null;

        const nbPaye = selectedAccount.nb_jours_payer || 0;
        const nbNonPaye = selectedAccount.nb_jours_non_payer || 0;
        const totalJours = selectedAccount.total_jours || 0;
        const duree = selectedAccount.duree_sol || selectedAccount.duree || 0;
        const montantNonPayer = selectedAccount.montant_non_payer || 0;
        const frequence = selectedAccount.frequence_paiement || selectedAccount.frequence || 'jours';

        if (duree === 0) return null;
        return {
            nbPaye,
            nbNonPaye,
            totalJours,
            duree,
            montantNonPayer,
            frequence,
            percentage: Math.min(100, (nbPaye / duree) * 100),
        };
    };

    const solProgress = getSolProgress();

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 p-6 overflow-y-auto bg-muted/30">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <Button variant="outline" size="icon" onClick={() => navigate('/transactions')}>
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                            <div>
                                <Breadcrumb>
                                    <BreadcrumbList>
                                        <BreadcrumbItem>
                                            <BreadcrumbLink href="/transactions">{t('transactions')}</BreadcrumbLink>
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                        <BreadcrumbItem>
                                            <BreadcrumbPage>{t('newTransaction.title')}</BreadcrumbPage>
                                        </BreadcrumbItem>
                                    </BreadcrumbList>
                                </Breadcrumb>
                                <h1 className="text-2xl font-bold mt-1">{t('newTransaction.title')}</h1>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Left Side - Account Search & Selection */}
                        <div className="lg:col-span-2 space-y-4">
                            {/* Account Search */}
                            {isLoadingAccount ? (
                                /* Loading State when fetching account details */
                                <Card className="border-border/60 shadow-none">
                                    <CardContent className="flex flex-col items-center justify-center py-12">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                                        <p className="text-muted-foreground">{t('newTransaction.search.loadingAccountDetails')}</p>
                                    </CardContent>
                                </Card>
                            ) : !selectedAccount ? (
                                <Card className="border-border/60 shadow-none">
                                    <CardHeader className="py-4">
                                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                                            <Search className="h-4 w-4" />
                                            {t('newTransaction.search.title')}
                                        </CardTitle>
                                        <CardDescription className="text-xs">
                                            {t('newTransaction.search.description')}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-3 pb-2">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                placeholder={t('newTransaction.search.placeholder')}
                                                className="pl-9 h-10 text-base"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                autoFocus
                                            />
                                            {isSearching && (
                                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />
                                            )}
                                        </div>

                                        {/* Search Results */}
                                        {searchResults.length > 0 && (
                                            <div className="border rounded-lg divide-y">
                                                {searchResults.map((compte) => (
                                                    <div
                                                        key={compte.id}
                                                        className="p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                                                        onClick={() => handleSelectAccount(compte)}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                                    <User className="h-5 w-5 text-primary" />
                                                                </div>
                                                                <div>
                                                                    <p className="font-semibold text-sm">
                                                                        {compte.client?.prenom} {compte.client?.nom}
                                                                    </p>
                                                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                                        <CreditCard className="h-3 w-3" />
                                                                        <span>{compte.code}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <Badge variant={compte.type === 'SOL' ? 'default' : 'secondary'} className="text-xs px-2 py-0.5">
                                                                    {compte.type}
                                                                </Badge>
                                                                <p className="text-sm font-bold mt-1">
                                                                    {formatAmount(compte.type === 'SOL' ? compte.soldeS : compte.soldeBS, compte.devise)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
                                            <div className="text-center py-8 text-muted-foreground">
                                                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                <p className="text-base">{t('newTransaction.search.noResults')}</p>
                                                <p className="text-xs">{t('newTransaction.search.tryAgain')}</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            ) : (
                                <>
                                    {/* Selected Account Card */}
                                    <Card className="border-border/60 shadow-none">
                                        <CardHeader className="py-4 pb-2">
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shrink-0">
                                                        <User className="h-6 w-6 text-primary" />
                                                    </div>
                                                    <div>
                                                        <CardTitle className="text-lg">
                                                            {selectedAccount.client?.prenom || selectedAccount.prenom} {selectedAccount.client?.nom || selectedAccount.nom}
                                                        </CardTitle>
                                                        <div className="flex flex-wrap items-center gap-2 mt-1">
                                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                                <CreditCard className="h-3.5 w-3.5" />
                                                                <span className="font-mono text-sm">{selectedAccount.code}</span>
                                                            </div>
                                                            <Badge variant="secondary" className="text-[10px] px-2 py-0 h-5 font-normal bg-primary/10 text-primary hover:bg-primary/20 border-0">
                                                                {selectedAccount.type || selectedAccount.type_compte}
                                                            </Badge>
                                                            <Badge variant="secondary" className={`text-[10px] px-2 py-0 h-5 font-normal border-0 ${(selectedAccount.statut || selectedAccount.etat) === 'actif' ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20' : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'}`}>
                                                                {selectedAccount.statut || selectedAccount.etat}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </div>
                                                <Button variant="outline" size="sm" onClick={() => setSelectedAccount(null)} className="w-full sm:w-auto h-8 text-xs">
                                                    {t('newTransaction.account.change')}
                                                </Button>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="pt-2 pb-4">
                                            <Separator className="mb-3" />

                                            {/* Balances Grid */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                                                {((selectedAccount.type || selectedAccount.type_compte) === 'BS') && (
                                                    <div className="bg-blue-50 dark:bg-blue-900/40 rounded-lg p-2.5 border-0">
                                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-blue-600/70 mb-0.5 uppercase tracking-wide">
                                                            <Wallet className="h-3 w-3" />
                                                            {t('newTransaction.account.balanceBS')}
                                                        </div>
                                                        <p className="text-base font-bold text-blue-700 dark:text-blue-400">
                                                            {formatAmount(selectedAccount.soldeBS ?? selectedAccount.solde_BS ?? 0, selectedAccount.devise)}
                                                        </p>
                                                    </div>
                                                )}

                                                {((selectedAccount.type || selectedAccount.type_compte) === 'SOL') && (
                                                    <>
                                                        <div className="bg-emerald-50 dark:bg-emerald-900/40 rounded-lg p-2.5 border-0">
                                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600/70 mb-0.5 uppercase tracking-wide">
                                                                <Wallet className="h-3 w-3" />
                                                                {t('newTransaction.account.balanceSOL')}
                                                            </div>
                                                            <p className="text-base font-bold text-emerald-700 dark:text-emerald-400">
                                                                {formatAmount(selectedAccount.soldeS ?? selectedAccount.solde_S ?? 0, selectedAccount.devise)}
                                                            </p>
                                                        </div>
                                                        <div className="bg-violet-50 dark:bg-violet-900/40 rounded-lg p-2.5 border-0">
                                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-violet-600/70 mb-0.5 uppercase tracking-wide">
                                                                <Calendar className="h-3 w-3" />
                                                                {t('newTransaction.account.amountPerDay')}
                                                            </div>
                                                            <p className="text-base font-bold text-violet-700 dark:text-violet-400">
                                                                {formatAmount(selectedAccount.montantJours ?? selectedAccount.montant_jours ?? 0, selectedAccount.devise)}
                                                            </p>
                                                        </div>
                                                        <div className="bg-orange-50 dark:bg-orange-900/40 rounded-lg p-2.5 border-0">
                                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-orange-600/70 mb-0.5 uppercase tracking-wide">
                                                                <Flame className="h-3 w-3" />
                                                                {t('newTransaction.account.nbMain')}
                                                            </div>
                                                            <p className="text-base font-bold text-orange-700 dark:text-orange-400">
                                                                {new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(selectedAccount.nbMain ?? parseFloat(selectedAccount.nb_main || '0') ?? 0)}
                                                            </p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* SOL Progress */}
                                            {solProgress && (
                                                <div className="mt-4 bg-muted/40 rounded-lg p-3 border-0">
                                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                                                        <span className="text-xs font-black uppercase tracking-wide flex items-center gap-1.5 text-foreground/80">
                                                            <TrendingUp className="h-3.5 w-3.5" />
                                                            {t('newTransaction.account.progression')}
                                                        </span>
                                                        <Badge variant="secondary" className="w-fit text-[10px] px-2 py-0.5 h-5 font-black bg-background text-foreground border-0 shadow-sm">
                                                            {solProgress.nbPaye} / {solProgress.duree} {solProgress.frequence}
                                                        </Badge>
                                                    </div>
                                                    <Progress value={solProgress.percentage} className="h-2 mb-3 bg-primary/20" />
                                                    <div className="grid grid-cols-3 gap-2 text-[10px]">
                                                        <div className="bg-emerald-100/50 dark:bg-emerald-900/40 rounded p-2 text-center border-0">
                                                            <p className="text-emerald-700 dark:text-emerald-400 font-black text-base">{solProgress.nbPaye}</p>
                                                            <p className="text-emerald-800/90 dark:text-emerald-300 uppercase font-black tracking-wider text-[10px]">{t('newTransaction.account.paid')}</p>
                                                        </div>
                                                        <div className="bg-red-100/50 dark:bg-red-900/40 rounded p-2 text-center border-0">
                                                            <p className="text-red-700 dark:text-red-400 font-black text-base">{solProgress.nbNonPaye}</p>
                                                            <p className="text-red-800/90 dark:text-red-300 uppercase font-black tracking-wider text-[10px]">{t('newTransaction.account.unpaid')}</p>
                                                        </div>
                                                        <div className="bg-orange-100/50 dark:bg-orange-900/40 rounded p-2 text-center border-0">
                                                            <p className="text-orange-700 dark:text-orange-400 font-black text-base">{formatAmount(solProgress.montantNonPayer, selectedAccount.devise)}</p>
                                                            <p className="text-orange-800/90 dark:text-orange-300 uppercase font-black tracking-wider text-[10px]">{t('newTransaction.account.remaining')}</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-[10px] font-bold text-muted-foreground text-center mt-2 bg-background/50 py-1 rounded w-full">
                                                        {t('newTransaction.account.total')}: {solProgress.duree} {solProgress.frequence} | {t('newTransaction.account.days')}: {solProgress.totalJours}
                                                    </div>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Last Transactions */}
                                    <Card className="border-border/60 shadow-none">
                                        <CardHeader className="py-3 pb-2">
                                            <div className="flex items-center justify-between">
                                                <CardTitle className="flex items-center gap-2 text-base">
                                                    <Clock className="h-4 w-4" />
                                                    {t('newTransaction.lastTransactions.title')}
                                                </CardTitle>
                                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => loadLastTransactions(selectedAccount.id)}>
                                                    <RefreshCcw className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="py-0 pb-2">
                                            {isLoadingTransactions ? (
                                                <div className="flex items-center justify-center py-4">
                                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                                </div>
                                            ) : lastTransactions.length === 0 ? (
                                                <div className="text-center py-6 text-muted-foreground">
                                                    <AlertCircle className="h-6 w-6 mx-auto mb-2 opacity-50" />
                                                    <p className="text-sm">{t('newTransaction.lastTransactions.noTransactions')}</p>
                                                </div>
                                            ) : (
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow className="hover:bg-transparent">
                                                            <TableHead className="h-8 text-xs">{t('date')}</TableHead>
                                                            <TableHead className="h-8 text-xs">{t('type')}</TableHead>
                                                            <TableHead className="h-8 text-xs text-right">{t('amount')}</TableHead>
                                                            <TableHead className="h-8 text-xs">{t('statusLabel')}</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {lastTransactions.map((item, index) => (
                                                            item.is_group ? (
                                                                // Grouped SOL transactions
                                                                <React.Fragment key={item.group_key || index}>
                                                                    <TableRow
                                                                        className="hover:bg-muted/30 cursor-pointer bg-blue-50/50 dark:bg-blue-900/20"
                                                                        onClick={() => toggleGroupExpansion(item.group_key!)}
                                                                    >
                                                                        <TableCell className="py-2 text-xs text-muted-foreground">
                                                                            {formatDate(item.latest_created_at!)}
                                                                        </TableCell>
                                                                        <TableCell className="py-2">
                                                                            <div className="flex items-center gap-1.5">
                                                                                <ArrowDownLeft className="h-3.5 w-3.5 text-green-500" />
                                                                                <span className="font-medium">{t('types.depo_sol')}</span>
                                                                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-blue-500/20 text-blue-600 border-0">
                                                                                    {t('transactions:groupedTransactions', { count: item.count })}
                                                                                </Badge>
                                                                            </div>
                                                                        </TableCell>
                                                                        <TableCell className="text-right font-mono font-bold text-green-600">
                                                                            <div className="flex flex-col items-end">
                                                                                <span>{formatAmount(parseFloat(item.total_montant!), 'HTG')}</span>
                                                                                {item.created_by_username && (
                                                                                    <span className="text-[9px] text-muted-foreground/60 italic font-normal">
                                                                                        {t('transactions:by')}{item.created_by_username}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="h-6 text-xs px-2"
                                                                            >
                                                                                {expandedGroups.has(item.group_key!) ? t('newTransaction.lastTransactions.close') : t('newTransaction.lastTransactions.showDetails')}
                                                                            </Button>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                    {/* Expanded individual transactions */}
                                                                    {expandedGroups.has(item.group_key!) && item.transactions?.map((tx) => (
                                                                        <TableRow key={tx.id} className="bg-blue-50/30 dark:bg-blue-900/10 border-l-2 border-blue-500">
                                                                            <TableCell className="py-1.5 text-[10px] text-muted-foreground pl-6">
                                                                                {formatDate(tx.created_at)}
                                                                            </TableCell>
                                                                            <TableCell className="py-1.5 text-xs text-muted-foreground pl-3">
                                                                                ↳ {t('types.depo_sol')}
                                                                            </TableCell>
                                                                            <TableCell className="text-right font-mono text-xs">
                                                                                <div className="flex flex-col items-end">
                                                                                    <span>{formatAmount(parseFloat(tx.montant), 'HTG')}</span>
                                                                                    {tx.created_by_username && (
                                                                                        <span className="text-[8px] text-muted-foreground/60 italic">
                                                                                            {t('transactions:by')}{tx.created_by_username}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Badge variant="secondary" className={`text-[9px] px-1.5 py-0 h-4 font-normal border-0 ${tx.status === 'reussi' ? 'bg-green-500/10 text-green-600' : 'bg-gray-500/10 text-gray-600'}`}>
                                                                                    {tx.status}
                                                                                </Badge>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </React.Fragment>
                                                            ) : (
                                                                // Single transaction
                                                                <TableRow key={item.id} className="hover:bg-muted/30">
                                                                    <TableCell className="py-2 text-xs text-muted-foreground">
                                                                        {formatDate(item.created_at!)}
                                                                    </TableCell>
                                                                    <TableCell className="py-2">
                                                                        <div className="flex items-center gap-1.5">
                                                                            {item.type.includes('depo') ? (
                                                                                <ArrowDownLeft className="h-3.5 w-3.5 text-green-500" />
                                                                            ) : (
                                                                                <ArrowUpRight className="h-3.5 w-3.5 text-red-500" />
                                                                            )}
                                                                            {getTypeLabel(item.type)}
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell className="text-right font-mono font-medium">
                                                                        <div className="flex flex-col items-end">
                                                                            <span>{formatAmount(parseFloat(item.montant!), 'HTG')}</span>
                                                                            {item.created_by_username && (
                                                                                <span className="text-[9px] text-muted-foreground/60 italic">
                                                                                    {t('transactions:by')}{item.created_by_username}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Badge variant="secondary" className={`text-[10px] px-2 py-0 h-5 font-normal border-0 ${item.status === 'reussi' ? 'bg-green-500/10 text-green-600' : item.status === 'echoue' ? 'bg-red-500/10 text-red-600' : 'bg-gray-500/10 text-gray-600'}`}>
                                                                            {item.status}
                                                                        </Badge>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            )}
                                        </CardContent>
                                    </Card>
                                </>
                            )}
                        </div>

                        {/* Right Side - Transaction Form */}
                        <div className="space-y-4">
                            <Card className={`border-border/60 shadow-none sticky top-4 ${!selectedAccount ? 'opacity-50 pointer-events-none' : ''}`}>
                                <CardHeader>
                                    <CardTitle>{t('newTransaction.title')}</CardTitle>
                                    <CardDescription>
                                        {t('newTransaction.form.description')}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        {/* Transaction Type */}
                                        <div className="space-y-2">
                                            <Label>{t('newTransaction.form.type')}</Label>
                                            <Select
                                                value={transactionType}
                                                onValueChange={(val) => setTransactionType(val as TransactionType)}
                                            >
                                                <SelectTrigger className="h-12">
                                                    <SelectValue placeholder={t('newTransaction.form.typePlaceholder')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {transactionOptions.map((option) => (
                                                        <SelectItem key={option.value} value={option.value}>
                                                            <div className="flex items-center gap-2">
                                                                {option.icon}
                                                                {option.label}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errors.type && (
                                                <p className="text-sm text-destructive">{errors.type}</p>
                                            )}
                                        </div>

                                        {/* Amount Input */}
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label>{t('newTransaction.form.amount')}</Label>
                                                {((selectedAccount?.type || selectedAccount?.type_compte) === 'SOL') && (selectedAccount?.montantJours || selectedAccount?.montant_jours) && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-xs h-7 px-2"
                                                        onClick={() => setUseNbMain(!useNbMain)}
                                                    >
                                                        {useNbMain ? t('newTransaction.form.directAmount') : t('newTransaction.form.useNbMain')}
                                                    </Button>
                                                )}
                                            </div>

                                            {useNbMain && ((selectedAccount?.type || selectedAccount?.type_compte) === 'SOL') ? (
                                                <div className="space-y-3">
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">{t('newTransaction.form.nbMainLabel')}</Label>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            placeholder="Ex: 5"
                                                            className="h-12 text-lg"
                                                            value={nbMain}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                setNbMain(val);
                                                                if (val && parseFloat(val) <= 0) {
                                                                    setErrors(prev => ({ ...prev, amount: t('newTransaction.errors.nbMainGreaterThanZero') }));
                                                                } else {
                                                                    setErrors(prev => {
                                                                        const { amount, ...rest } = prev;
                                                                        return rest;
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">{t('newTransaction.form.calculatedAmount')}</Label>
                                                        <Input
                                                            type="number"
                                                            placeholder={t('newTransaction.form.amountPlaceholder')}
                                                            value={amount}
                                                            disabled
                                                            className="h-12 text-lg bg-muted font-bold"
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="relative">
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        placeholder={t('newTransaction.form.amountPlaceholder')}
                                                        className={`h-12 text-lg pr-16 ${errors.amount ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                                        value={amount}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setAmount(val);
                                                            if (val && parseFloat(val) <= 0) {
                                                                setErrors(prev => ({ ...prev, amount: t('newTransaction.errors.amountGreaterThanZero') }));
                                                            } else {
                                                                setErrors(prev => {
                                                                    const { amount, ...rest } = prev;
                                                                    return rest;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                                                        {selectedAccount?.devise || 'HTG'}
                                                    </span>
                                                </div>
                                            )}
                                            {errors.amount && (
                                                <p className="text-sm text-destructive">{errors.amount}</p>
                                            )}
                                        </div>

                                        {/* Submit Button */}
                                        <Button
                                            type="submit"
                                            className="w-full h-12 text-lg"
                                            disabled={!selectedAccount || isSubmitting}
                                        >
                                            {isSubmitting && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                                            <CheckCircle2 className="mr-2 h-5 w-5" />
                                            {t('newTransaction.form.submit')}
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>

                            {/* Last Transaction Quick View */}
                            {dernierTransaction && (
                                <Card className="bg-gradient-to-br from-muted/50 to-muted/30">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm font-medium text-muted-foreground">
                                            {t('transactions:lastTransaction')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">
                                                    {dernierTransaction.is_group
                                                        ? t('transactions:groupedTransactions', { count: dernierTransaction.count })
                                                        : getTypeLabel(dernierTransaction.type)}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    {formatDate(dernierTransaction.is_group
                                                        ? dernierTransaction.latest_created_at!
                                                        : dernierTransaction.created_at!)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xl font-bold text-primary">
                                                    {formatAmount(
                                                        parseFloat(dernierTransaction.is_group
                                                            ? dernierTransaction.total_montant!
                                                            : dernierTransaction.montant!),
                                                        'HTG'
                                                    )}
                                                </p>
                                                {dernierTransaction.created_by_username && (
                                                    <p className="text-[10px] text-muted-foreground italic text-right mb-1">
                                                        {t('transactions:by')}{dernierTransaction.created_by_username}
                                                    </p>
                                                )}
                                                {!dernierTransaction.is_group && (
                                                    <Badge variant={dernierTransaction.status === 'reussi' ? 'default' : 'secondary'}>
                                                        {dernierTransaction.status}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Dialog */}
            <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                            Konfime Tranzaksyon
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-4">
                                <p>Tanpri verifye detay tranzaksyon an:</p>
                                <div className="bg-muted rounded-lg p-4 space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Kont:</span>
                                        <span className="font-medium">{selectedAccount?.code}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Kliyan:</span>
                                        <span className="font-medium">
                                            {selectedAccount?.client?.prenom || selectedAccount?.prenom} {selectedAccount?.client?.nom || selectedAccount?.nom}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Tip:</span>
                                        <span className="font-medium">
                                            {transactionOptions.find(o => o.value === transactionType)?.label}
                                        </span>
                                    </div>
                                    <Separator />
                                    {/* Detailed Breakdown */}
                                    <div className="space-y-2 pt-2 text-sm">
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Balans aktyèl:</span>
                                            {selectedAccount && (
                                                <span>
                                                    {formatAmount(
                                                        selectedAccount.type === 'SOL' 
                                                            ? (selectedAccount.soldeS ?? selectedAccount.solde_S ?? 0) 
                                                            : (selectedAccount.soldeBS ?? selectedAccount.solde_BS ?? 0),
                                                        selectedAccount.devise
                                                    )}
                                                </span>
                                            )}
                                        </div>

                                        {/* Montant requested */}
                                        <div className="flex justify-between">
                                            <span>{transactionType.includes('depo') ? 'Montan Depo:' : 'Montan Retrait:'}</span>
                                            <span className={`font-medium ${transactionType.includes('depo') ? 'text-green-600' : 'text-red-600'}`}>
                                                {transactionType.includes('depo') ? '+' : '-'} {formatAmount(parseFloat(amount || '0'), selectedAccount?.devise)}
                                            </span>
                                        </div>

                                        {/* Fees for withdrawal */}
                                        {transactionType === 'retrait_bs' && feeAmount > 0 && (
                                            <div className="flex justify-between text-destructive">
                                                <span>Frè ({feePercentage}%):</span>
                                                <span>- {formatAmount(feeAmount, selectedAccount?.devise)}</span>
                                            </div>
                                        )}

                                        <Separator className="my-1" />

                                        <div className="flex justify-between font-bold">
                                            <span>Nouvo Balans:</span>
                                            {selectedAccount && amount && (
                                                <span className={transactionType.includes('depo') ? 'text-green-600' : 'text-red-600'}>
                                                    {formatAmount(
                                                        (() => {
                                                            const current = selectedAccount.type === 'SOL' 
                                                                ? (selectedAccount.soldeS ?? selectedAccount.solde_S ?? 0) 
                                                                : (selectedAccount.soldeBS ?? selectedAccount.solde_BS ?? 0);
                                                            
                                                            if (transactionType.includes('depo')) {
                                                                return current + parseFloat(amount);
                                                            } else {
                                                                return current - totalDebit; // Includes fees
                                                            }
                                                        })(),
                                                        selectedAccount.devise
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-center">
                                    {transactionType.includes('retrait')
                                        ? `⚠️ Total ${formatAmount(totalDebit, selectedAccount?.devise)} (montan + frè) ap dedwi sou kont kliyan an.`
                                        : "✅ Montan sa a ap ajoute sou kont kliyan an."}
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annule</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={executeTransaction}
                            className={`${transactionType.includes('depo') ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white border-0`}
                        >
                            Konfime
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div >
    );
}
